<template>
  <div>
    <HeaderBreadcrumbs :title="'All Accounts'" :items="[
      { label: 'Accounts' },
    ]" />
    <div class="bg-white p-0 my-3 rounded-lg shadow-sm">
      <div class="flex px-4 py-3 justify-between items-center">
        <div class="relative">
          <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" width="20" height="20"
            fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10 2a8 8 0 016.32 12.906l4.387 4.387a1 1 0 01-1.415 1.415l-4.387-4.387A8 8 0 1110 2zm0 2a6 6 0 100 12 6 6 0 000-12z" />
          </svg>
          <input placeholder="Search Account Email" class="p-2.5 pl-5 w-96 text-sm text not-italic border rounded-lg"
            v-model="filter" v-on:keyup.enter="debounceSearch" />
          <button v-if="filter" class="absolute top-1/2 right-3 transform -translate-y-1/2  text-gray-500 active:border"
            @click="reset" title="Clear search">
            ✕
          </button>
        </div>
        <div>
          <t-dropdown>
            <div slot="trigger" slot-scope="{ mousedownHandler, focusHandler, blurHandler, keydownHandler }"
              class="flex z-50">
              <button
                class="flex items-center relative justify-center h-10 px-3 py-2.5 text-gray-500 bg-white border border-gray-400 rounded-lg"
                @mousedown="mousedownHandler" @focus="focusHandler" @blur="blurHandler" @keydown="keydownHandler">
                <svg class="w-4 h-4 mr-1 text-gray-600 dark:text-white" aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M5.05 3C3.291 3 2.352 5.024 3.51 6.317l5.422 6.059v4.874c0 .472.227.917.613 1.2l3.069 2.25c1.01.742 2.454.036 2.454-1.2v-7.124l5.422-6.059C21.647 5.024 20.708 3 18.95 3H5.05Z" />
                </svg>
                Filter
                <svg class="w-5 h-5 ml-1 text-gray-600 fill-current" viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg">
                  <polygon
                    points="9.29289322 12.9497475 10 13.6568542 15.6568542 8 14.2426407 6.58578644 10 10.8284271 5.75735931 6.58578644 4.34314575 8" />
                </svg>
                <div v-if="dataBadge"
                  class="rounded-full w-5 h-5 bg-red-600 absolute -top-2 -right-2 text-white text-xs flex justify-center items-center">
                  {{ dataBadge }}
                </div>
              </button>
            </div>
            <div class="w-custom-user bg-white absolute -right-2 pt-2 pb-3 border rounded-lg"
              slot-scope="{ hide, blurHandler }">
              <div
                class="block w-full px-4 py-2 text-sm text-gray-500 transition duration-150 ease-in-out focus:outline-none focus:bg-gray-100"
                role="menuitem">
                <div class="flex justify-between">
                  <div>Filter</div>
                  <div @click="hide">
                    <svg class="w-4 h-4 text-gray-800 dark:text-white cursor-pointer" aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M6 18 17.94 6M18 18 6.06 6" />
                    </svg>
                  </div>
                </div>
                <div class="flex justify-between mb-3 mt-2">
                  <div class="w-full mr-2">
                    <label class="text-gray-600 font-semibold mb-3 mt-2">Verification Status</label>
                    <FilterComponent v-model="verificationStatus" :options="verificationStatusOptions"
                      :defaultText="'Select Verification Status'" />
                  </div>
                  <div class="w-full ml-2">
                    <label class="text-gray-600 font-semibold mb-3 mt-2">Status</label>
                    <FilterComponent v-model="itemStatus" :options="statusOptions" :defaultText="'Select Status'" />
                  </div>
                </div>
                <div class="mt-3">
                  <button class="border bg-blue-500 px-4 py-2 text-white rounded-lg" @click="asyncFind()"
                    @blur="blurHandler">
                    Filter
                  </button>
                  <button class="border bg-white text-gray-500 px-4 py-2 rounded-lg ml-2" @click="reset()"
                    @blur="blurHandler">Reset</button>
                </div>

              </div>

            </div>
          </t-dropdown>
        </div>
      </div>
      <Table :headers="headers" :data="users" :row-key="[
        'email',
        'is_verified',
        'created',
        'last_login',
        'status']" :current-page="currentPage" :isLoading="isLoading" :per-page="perPage">
        <template #header-register="{ column }">
          <div class="flex items-center gap-1">
            {{ column.label }}
            <button @click="sortBy(column.key)" class="text-xs text-gray-500">
              <span href="#"><svg class="w-3 h-3 ms-1.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087 1.9 1.9 0 0 0 .11 1.985l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z" />
                </svg>
              </span>
            </button>
          </div>
        </template>
        <template #email="{ value }">
          <div class="flex lowercase items-center gap-1">
            {{ value }}
          </div>
        </template>
        <template #header-login="{ column }">
          <div class="flex items-center gap-1">
            {{ column.label }}
            <button @click="sortBy(column.key)" class="text-xs text-gray-500">
              <span href="#"><svg class="w-3 h-3 ms-1.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087 1.9 1.9 0 0 0 .11 1.985l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z" />
                </svg>
              </span>
            </button>
          </div>
        </template>

        <template #is_verified="{ row }">
          <div class="px-2 py-1 text-center rounded-lg text-xs font-medium capitalize flex justify-start items-center">
            <div v-if="row.verification_status === 'verified'"
              class="bg-blue-100 text-blue-800 mr-2 p-1 px-2  rounded-lg">
              Verified
            </div>
            <div v-else-if="row.verification_status === 'unverified'"
              class="bg-yellow-100 mr-2 text-yellow-800 p-1 px-2 rounded-lg">
              unverified
            </div>
            <div v-if="row.verification_status === 'banned'" class="bg-red-100  text-red-800 mr-2 p-1 px-2 rounded-lg">
              banned
            </div>
            <!-- <button v-if="showCopyButton(row)" class="ml-0" @click="copyTextToClipboard(UrlVerify + row.key_verify)">
              <i class="fa fa-copy"></i>
            </button> -->
          </div>
        </template>
        <template #actions="{ row }">
          <t-dropdown>
            <div slot="trigger" slot-scope="{
                mousedownHandler,
                focusHandler,
                blurHandler,
                keydownHandler,
                isShown
              }" class=" relative">
              <button id="user-menu" :class="{ ' text-gray-500 ': isShown }" aria-label="User menu" aria-haspopup="true"
                @focus.stop="focusHandler" @blur.stop="blurHandler" @keydown.stop="keydownHandler"
                @click.stop="mousedownHandler" class="cursor-pointer focus:outline-none">
                <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                  width="24" height="24" fill="none" viewBox="0 0 24 24">
                  <path stroke="currentColor" stroke-linecap="round" stroke-width="2"
                    d="M6 12h.01m6 0h.01m5.99 0h.01" />
                </svg>
              </button>
            </div>
            <div slot-scope="{ hide, blurHandler }" class="">
              <button
                class="block text-left w-full px-3 py-2 text-sm leading-5 text-gray-500 transition duration-150 ease-in-out hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                role="menuitem" @click.stop="hide" @blur="blurHandler">

                <router-link :to="{ path: '/customers/detail/' + row.email }"
                  class="flex justify-start hover:no-underline hover:text-gray-500 text-gray-500 items-center w-full">
                  <svg class="w-5 h-5 text-gray-500 dark:text-white" aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                    <path fill-rule="evenodd"
                      d="M4.998 7.78C6.729 6.345 9.198 5 12 5c2.802 0 5.27 1.345 7.002 2.78a12.713 12.713 0 0 1 2.096 2.183c.253.344.465.682.618.997.14.286.284.658.284 1.04s-.145.754-.284 1.04a6.6 6.6 0 0 1-.618.997 12.712 12.712 0 0 1-2.096 2.183C17.271 17.655 14.802 19 12 19c-2.802 0-5.27-1.345-7.002-2.78a12.712 12.712 0 0 1-2.096-2.183 6.6 6.6 0 0 1-.618-.997C2.144 12.754 2 12.382 2 12s.145-.754.284-1.04c.153-.315.365-.653.618-.997A12.714 12.714 0 0 1 4.998 7.78ZM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                      clip-rule="evenodd" />
                  </svg>
                  <span class="ml-2 text-sm">
                    View Detail
                  </span>
                </router-link>
              </button>
              <button
                class=" text-left w-full flex justify-start items-center px-4 py-2 text-sm  leading-5 text-gray-500 transition duration-150 ease-in-out hover:no-underline hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                role="menuitem" @click.stop="hide" @click="showModal(row, 'reset-password')" @blur="blurHandler">
                <i class="relative -left-1 fa-solid fa-key"></i>
                <span class="ml-1.5 text-sm">
                  Send Reset Password
                </span>
              </button>
              <button
                class=" block text-left w-full px-4 py-2 text-sm leading-5 text-gray-500 transition duration-150 ease-in-out hover:no-underline hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                role="menuitem" @click.stop="hide" @blur="blurHandler">

                <i class=" relative -left-1 fa-solid fa-envelope"></i>
                <span class="ml-1 text-sm" @click="showModal(row, 'verification')"
                  :disabled="row.is_verified || !row.roles.role_id"
                  :class="row.is_verified || !row.roles.role_id ? 'opacity-50 pointer-events-none' : 'opacity-100'">
                  Send Verification Email
                </span>
              </button>

            </div>
          </t-dropdown>
        </template>
        <template #created="{ value }">
          <span class="text-sm">{{ formatDate(value).date }}</span>
          <br>
          <span class="italic text-xs">{{ formatDate(value).time }}</span>
        </template>
        <template #last_login="{ value }">
          <span>{{ formatDate(value).date }}</span>
          <br>
          <span class="italic text-xs">{{ formatDate(value).time }}</span>
        </template>
        <template #status={value}>
          <div style="" class="text-center py-1 px-1.5 rounded-lg w-auto"
            :class="{ 'bg-green-100': value === 'active', 'bg-gray-100': value === 'inactive' }">
            {{ value }}
          </div>

        </template>
      </Table>
      <t-modal ref="modalAction">
        <div class="text-sm flex-col justify-center items-center text-center">
          <p class="text-base font-semibold text-gray-900 mb-2">Are you sure?</p>

          <div class="text-base text-gray-500">
            You will send
            <span v-if="modalActionType === 'reset-password'">reset password</span>
            <span v-else-if="modalActionType === 'verification'">email verification</span>
            to
          </div>

          <p class="text-gray-900">{{ selectedUser.email }}</p>

          <div class="mt-2 flex justify-center items-center">
            <button @click="handleModalAction" :disabled="loadingBtnModal"
              :class="loadingBtnModal ? 'opacity-50 pointer-events-none' : ''"
              class="bg-blue-700 px-2.5 py-2 text-white rounded-lg">
              {{ loadingBtnModal ? 'Loading ...' : 'Save' }}
            </button>
            <button @click="cancelBtn" class="bg-white border ml-2 px-2.5 py-2 text-gray-500 rounded-lg">Cancel</button>
          </div>
        </div>
      </t-modal>

      <div v-if="totalRows / perPage > 1" class="flex justify-between ml-1 px-3 items-start mt-3">
        <div class="font-semibold mt-1">
          <span class="text-gray-500 font-normal">Showing </span> {{ currentPage }}-{{ perPage }} <span
            class="text-gray-500 font-normal">of</span> {{ parseInt(totalRows) }}
        </div>
        <div class="mb-2">
          <t-pagination :total-items="Number(totalRows)" :per-page="perPage" v-model="currentPage" class="rounded-lg" />
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import { mapState, mapActions } from 'vuex';
import API from '../../plugins/http';
import constant from '../../store/constant';
import moment from 'moment';
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs.vue';
import FilterComponent from '@/components/form/StatusFilterComponent.vue'
import { EventBus } from '../../event-bus'
import Table from '@/components/form/table'
const UrlVerify = process.env.VUE_APP_EBOOKS_URL + 'id/email-verification/';
export default {
  name: 'users',
  components: {
    HeaderBreadcrumbs,
    FilterComponent,
    Table
  },
  data() {
    return {
      UrlVerify,
      currentPage: 1,
      perPage: 15,
      filter: null,
      dataBadge: null,
      sorts: [],
      modalActionType: '',
      isLoadingSearch: false,
      loadingBtnModal: false,
      sortString:[],
      items_search: [],
      selectedUser: {
        id: null,
        email: ''
      },
      headers: [
        { label: 'no', key: '' },
        { label: 'Account email', key: 'email' },
        { label: 'Verification status', key: 'is_verified' },
        { label: 'register Date', key: 'created', slotHeader: 'header-register' },
        { label: 'last login Date', key: 'last_login', slotHeader: 'header-login' },
        { label: 'Status', key: 'is_active', },
      ],
      itemStatus: null,
      verificationStatus: null,
      statusOptions: [
        {
          text: 'All Status',
          value: null,
        },
        {
          text: 'Active',
          value: 'active',
        },
        {
          text: 'Inactive',
          value: 'inactive',
        },

      ],
      verificationStatusOptions: [
        {
          text: 'All Type',
          value: null,
        },
        {
          text: 'Verified',
          value: 'verified',
        },
        {
          text: 'Unverified',
          value: 'unverified',
        },
        {
          text: 'Banned',
          value: 'banned',
        },
      ],
      verifiedUser: constant.USER_ROLE.VERIFIED,
      unVerifiedUser: constant.USER_ROLE.UNVERIFIED,
      bannedUser: constant.USER_ROLE.BANNED,
    };
  },
  watch: {
    currentPage: function () {
      this.actionGetUsers();
    },
    $route: {
      immediate: true,
      handler(to) {
        document.title =
          to.meta.title ||
          'Account | Content Management System Ebooks Gramedia.com';
      },
    },
  },
  computed: {
    ...mapState({
      isLoading: (state) => state.users.isLoading,
      isError: (state) => state.users.isError,
      totalRows: (state) => state.users.totalRows,
      users: (state) => state.users.items,
    }),
  },
  mounted() {
    this.actionGetUsers();
  },
  methods: {
    ...mapActions('users', [
      'fetchUsers',
      'searchUsers',
      'fetchUserById',
      'resetPassword',
    ]),
    //  ...mapMutations('users', ['setItems', 'setRows']),

    actionGetUsers() {
      let payload = {
        page: this.currentPage,
        limit: this.perPage,
        verification_status: this.verificationStatus,
        status: this.itemStatus,
        email: this.filter,
        sort: this.sortString
      };
      this.fetchUsers(payload);
      this.filterBadge()
    },
    filterBadge() {
      let badgeCount = 0;
      badgeCount += [this.itemStatus, this.verificationStatus].filter(status => status != null).length;

      this.dataBadge = badgeCount > 0 ? badgeCount : null;
    },
    debounceSearch(event) {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.typing = null;
        this.filter = event.target.value;
        if (!this.isLoading) {
          this.asyncFind();
        }
      }, 800);
    },
    reset() {
      this.filter = ''
      this.itemStatus = null
      this.verificationStatus = null
      this.actionGetUsers()
    },
    //  showCopyButton(item) {
    //    return (
    //      item.verification_status === 'unverified'
    //    );
    //  },
    sortBy(column) {
       let existingIndex = this.sorts.findIndex(s => s.column === column);
      if (existingIndex >= 0) {
        this.sorts[existingIndex].order =
          this.sorts[existingIndex].order === "asc" ? "desc" : "asc";

        const [item] = this.sorts.splice(existingIndex, 1);
        this.sorts.unshift(item);
      } else {
        this.sorts.unshift({ column, order: "asc" });
      }

      this.sortString = this.sorts
        .map(s => `${s.column}_${s.order}`)
        .join(',');

      let payload = {
        page: this.currentPage,
        limit: this.perPage,
        verification_status: this.verificationStatus,
        status: this.itemStatus,
        sort: this.sortString
      };
      this.fetchUsers(payload);
    },
    formatDate(tgl) {
      return {
        date: moment(tgl).format('DD MMM YY'),
        time: moment(tgl).format('HH:mm:ss'),
      };
    },
    handleModalResetBtn(id, email) {
      this.selectedUser = { id, email };
      this.$refs.modalResetPassword.show();
    },

    showModal(user, type) {
      this.selectedUser = user;
      this.modalActionType = type;
      this.$refs.modalAction.show();
    },
    cancelBtn() {
      this.$refs.modalAction.hide();
      this.modalActionType = ''
    },
    async handleModalAction() {
      if (this.modalActionType === 'reset-password') {
        await this.handleSaveResetPassword(this.selectedUser);
      } else if (this.modalActionType === 'verification') {
        await this.handleSendVerification(this.selectedUser);
      }
    },
    formatPrice(value, currency) {
      if (value) {
        let formatter = null;
        if (currency == 'USD') {
          formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
          });
        } else {
          formatter = new Intl.NumberFormat('en-ID', {
            style: 'currency',
            currency: currency,
          });
        }

        return formatter.format(value);
      }

      return '0';
    },
    async asyncFind() {
      try {
        this.isLoadingSearch = true;
        //   this.currentPage = 1;
        let data = {
          page: this.currentPage = 1,
          limit: this.perPage,
          email: this.filter,
          verification_status: this.verificationStatus,
          status: this.itemStatus,
          sort: this.sortString
        };

        await this.fetchUsers(data);
        // console.log(info);
      } catch (error) {
        console.log(error);
      }
      this.filterBadge()
    },
    handleSaveResetPassword(user) {
      this.loadingBtnModal = true
      this.modalActionType = ''
      this.resetPassword({ id: user.id })
        .then(async (res) => {
          if (res.status == 200) {
            this.loadingBtnModal = false
            await this.$refs.modalAction.hide();

            EventBus.$emit('show-toast', {
              message: `You will send email reset password`,
              type: 'success',
            });
            // this.copyToClipboard(res.data.data);
            // this.copyTextToClipboard(res.data.data)
          }
        })
        .catch((err) => {
          console.log(err.response.data.message)
        });
    },
    handleSendVerification(user) {
      this.modalActionType = ''
      this.loadingBtnModal = true
      API.get('users/resend-email-verification/' + user.id)
        .then((res) => {
          if (res.status == 200) {
            this.loadingBtnModal = false
            this.$refs.modalAction.hide();
            EventBus.$emit('show-toast', {
              message: `Email verification has been successfully sent.`,
              type: 'success',
            });
          }
        })
        .catch((err) => {
          EventBus.$emit('show-toast', {
            message: err.response.data.data.message,
            type: 'success',
          });
            this.$refs.modalAction.hide();
             this.loadingBtnModal = false
        });
    },

    formatDateString(date) {
      return new Date(date).toString();
    },
    formatPriceBasic(value) {
      let val = (value / 1).toFixed(0).replace('.', ',');
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text);
      this.$swal({
        toast: 'true',
        position: 'top-center',
        title: 'click button to copy url',
        timer: 5000,
      });
    },
  },
};
</script>
<style>
.w-custom-user {
  width: 580px;
}
</style>
